import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import FilterListOffOutlinedIcon from '@mui/icons-material/FilterListOffOutlined';
import filterStyles from './filterStyles';
import { ActionLogsTableFilterDataType } from '../../types/filterTypes';

type FilterKey = keyof ActionLogsTableFilterDataType;

interface FilterFieldConfig {
  key: FilterKey;
  label: string;
}

const FILTER_FIELDS: readonly FilterFieldConfig[] = [
  { key: 'device_ip', label: 'Device IP' },
  { key: 'device_vendor', label: 'Device vendor' },
  { key: 'region', label: 'Region' },
  { key: 'user_action', label: 'User action' },
  { key: 'user_email', label: 'Email' },
  { key: 'building_code', label: 'Building code' },
];

interface ActionLogsTableFilterMenuProps {
  filterState: ActionLogsTableFilterDataType;
  activeFilterCount: number;
  onFilterChange: (key: FilterKey, value: string) => void;
  onClearFilter: (key: FilterKey) => void;
  onClearAll: () => void;
}

export default function ActionLogsTableFilterMenu(props: ActionLogsTableFilterMenuProps) {
  const { filterState, activeFilterCount, onFilterChange, onClearFilter, onClearAll } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const hasActiveFilters = activeFilterCount > 0;

  const handleClickFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  const handleFilterInput = (filterName: FilterKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange(filterName, e.target.value);
  };

  const handleClearFilter = (filterName: FilterKey) => () => {
    onClearFilter(filterName);
  };

  return (
    <React.Fragment>
      <Tooltip title="Apply filters">
        <IconButton
          onClick={handleClickFilter}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={open ? 'filter-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {hasActiveFilters ? (
            <Badge variant="dot" color="primary">
              <FilterListIcon color="primary" fontSize={'large'} />
            </Badge>
          ) : (
            <FilterListIcon color={open ? 'primary' : 'action'} fontSize={'large'} />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Clear filters">
        <IconButton size="small" onClick={onClearAll}>
          <FilterListOffOutlinedIcon
            color={hasActiveFilters ? 'primary' : 'action'}
            fontSize={'large'}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="filter-menu"
        open={open}
        onClose={handleCloseFilter}
        slotProps={{
          paper: {
            sx: { ...filterStyles.menu },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem sx={{ display: 'flex' }}>
          <Button sx={{ ...filterStyles.actionButton }} variant="outlined" onClick={onClearAll}>
            <FilterListOffOutlinedIcon fontSize={'small'} /> Clear
          </Button>
          <Button
            sx={{ ...filterStyles.actionButton }}
            variant="outlined"
            onClick={handleCloseFilter}
          >
            Close
          </Button>
        </MenuItem>
        <Divider />
        {FILTER_FIELDS.map((field) => {
          const value = filterState[field.key] ?? '';
          const label = value ? `${field.label}: ${value}` : field.label;
          return (
            <MenuItem
              key={field.key}
              onKeyDown={(e) => {
                // Block Menu's keyboard nav while typing, but let Escape
                // bubble up so it still closes the menu.
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
            >
              <TextField
                label={label}
                value={value}
                size="small"
                sx={{ ...filterStyles.textField }}
                onChange={handleFilterInput(field.key)}
              />
              <IconButton onClick={handleClearFilter(field.key)}>
                <CloseIcon />
              </IconButton>
            </MenuItem>
          );
        })}
      </Menu>
    </React.Fragment>
  );
}
